import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  DSButton,
  DSDropdown,
  DSPagination,
  Table as DSTable,
  TableAlign
} from '@vr/ds-react'

import ModalError from '@/components/modal-error'
import ModalSuccess from '@/components/modal-success'
import ModalConfirmationDelivery from '@/compositions/modal-confirmation-delivery'
import { TableActionsDropdown } from '@/compositions/table-actions-dropdown'
import { DELIVERY_LIST_COLUMNS } from '@/constants/delivery-list-columns'
import { useDeliveryContext } from '@/contexts/delivery.context'
import { useUnitsListDelivery } from '@/hooks/use-units-list-delivery'

export const DeliveryUnitsList = ({ delivery }: { delivery: any }) => {
  const [isModalFilesOpen, setIsModalFilesOpen] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false)
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false)

  const { deliveryRefetchTrigger } = useDeliveryContext()

  const {
    handleLimitChange: onLimitChange,
    limit,
    refetch,
    results,
    setPage,
    tableData
  } = useUnitsListDelivery(delivery)

  useEffect(() => {
    if (deliveryRefetchTrigger > 0) {
      refetch()
    }
  }, [deliveryRefetchTrigger])

  const handleClick = () => {
    setIsModalFilesOpen(true)
  }

  const mappedData = useMemo(() => {
    return tableData.map(item => {
      return {
        ...item,
        operadora: item.nome
      }
    })
  }, [tableData])

  const handleLimitChange = useCallback(
    (selectedOptions: string | number | (string | number)[]) => {
      let value: string | number | undefined
      if (Array.isArray(selectedOptions)) {
        value = selectedOptions[0]
      } else {
        value = selectedOptions
      }
      const newLimit =
        typeof value === 'number' ? value : Number.parseInt(value, 10)
      onLimitChange(newLimit)
    },
    [onLimitChange]
  )

  return (
    <>
      <DSTable css={{ width: '100%', marginTop: '20px' }}>
        <DSTable.Head>
          <DSTable.Cell colSpan={6} align='right'>
            <>
              <DSButton
                variant='ghost'
                size='small'
                outlined
                css={{ marginRight: '16px' }}
                onClick={handleClick}
              >
                Confirmar entrega
              </DSButton>
              <DSDropdown
                options={[
                  { label: '10' },
                  { label: '25' },
                  { label: '50' },
                  { label: '100' }
                ]}
                onSelectionChange={handleLimitChange}
                css={{ padding: '16px 0' }}
              >
                <DSButton
                  variant='secondary'
                  size='small'
                  iconRight='principal-chevron-down'
                >
                  {`Mostrar: ${limit}`}
                </DSButton>
              </DSDropdown>
            </>
          </DSTable.Cell>
        </DSTable.Head>
        <DSTable.Head>
          {DELIVERY_LIST_COLUMNS.map(item => (
            <DSTable.Cell
              key={item.field}
              id={item.field}
              align={item.align as TableAlign}
            >
              {item.headerName}
            </DSTable.Cell>
          ))}
        </DSTable.Head>
        <DSTable.Body>
          {mappedData.map(item => (
            <DSTable.Row key={item.id} id={item.id?.toString()}>
              {DELIVERY_LIST_COLUMNS.map(key => {
                let cellContent: any
                if (key.field === 'acoes') {
                  cellContent = (
                    <TableActionsDropdown
                      pageResource='delivery'
                      unit={item}
                      files={item.arquivoDeIntegracao}
                      id={delivery.id}
                    />
                  )
                } else if (['nomeUnidade', 'nomeColaborador', 'nomeOperadora'].includes(key.field)) {
                  cellContent = (
                    <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {item[key.field as keyof typeof item]}
                    </span>
                  )
                } else {
                  cellContent = item[key.field as keyof typeof item]
                }
                return (
                  <DSTable.Cell
                    key={key.field}
                    align={key.align as TableAlign}
                  >
                    {cellContent}
                  </DSTable.Cell>
                )
              })}
            </DSTable.Row>
          ))}
        </DSTable.Body>
      </DSTable>

      <DSPagination
        total={results}
        maxPerPage={limit}
        handleChange={(newPage: number) => setPage(newPage)}
        nextPreview
        siblingCount={1}
      />

      <ModalConfirmationDelivery
        isOpen={isModalFilesOpen}
        onClose={() => setIsModalFilesOpen(false)}
        delivery={delivery}
        setShowErrorModal={setShowErrorModal}
        setShowSuccessModal={setShowSuccessModal}
      />

      <ModalSuccess
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title='Entrega confirmada com sucesso!'
      >
        A entrega foi confirmada com sucesso.
      </ModalSuccess>

      <ModalError
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title='Erro ao confirmar entrega!'
      >
        Ocorreu um erro ao confirmar entrega. Por favor, tente novamente!
      </ModalError>
    </>
  )
}
